import React, { useState, useEffect, useCallback } from "react";
import { useIntl } from "react-intl";

import {
  getOperationCarLinking,
  operationGetCarLayers,
  operationGetGlebas
} from "~/api";
import "./styles.scss";
import { formatNumber } from "~/utils/format";
import { useOperation } from "~/hooks/useOperation";
import UnlinkedCar from "./components/UnlinkedCar";
import LinkedCar from "./components/LinkedCar";

const CarItem = ({ car, carIndex, selectedCarId, setSelectedCar }) => {
  const { formatMessage } = useIntl();

  return (
    <React.Fragment>
      <div
        className={`car-list-item ${
          selectedCarId === car.gid ? "item-selected" : ""
        }`}
        onClick={() => {
          setSelectedCar(car);
        }}
      >
        <div className={`car-list-item-info`}>
          <p>
            CAR {carIndex + 1} - {formatMessage({ id: "area" })}{" "}
            {formatNumber(car.num_area)} ha
          </p>

          <span>{car.cod_imovel || formatMessage({ id: "sem-titulo" })}</span>
        </div>
      </div>
    </React.Fragment>
  );
};

const OperationCarLinking = ({ stepLabel, setError }) => {
  const { formatMessage } = useIntl();
  const { operation } = useOperation();
  const [loading, setLoading] = useState(true);
  const [layersLinkedCars, setLayersLinkedCars] = useState([]);
  const [areas, setAreas] = useState([]);
  const [carList, setCarList] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);

  const fetchResults = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await getOperationCarLinking(operation.id);
      const result = data.result;

      let list = result.detalhes;
      if (!Array.isArray(list)) {
        list = result.cars;
      }

      setSelectedCar(null);

      const hasLinked = list.map(car => car.is_vinculado).some(Boolean);
      if (hasLinked) {
        const response = await operationGetCarLayers(operation.id, 1);
        const carLayersResult = response.data.result;

        const responseGleba = await operationGetGlebas(operation.id);
        const glebasResult = responseGleba.data.result;

        setLayersLinkedCars(carLayersResult);
        setAreas(glebasResult);
      }

      setCarList(list);

      setLoading(false);
    } catch (e) {
      setError(e?.response?.data?.message || formatMessage({ id: "erro-api" }));
    }
  }, [operation.id, formatMessage, setError]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  let selectedCarArea = null;
  if (selectedCar) {
    layersLinkedCars.forEach(layerLinkedCar => {
      const sanitizeCar = string => string.replace(/-/g, "");

      const layerLinkedCarNumber = sanitizeCar(layerLinkedCar.B3Car.numero);
      const selectedCarNumber = sanitizeCar(selectedCar.cod_imovel);
      if (layerLinkedCarNumber === selectedCarNumber) {
        selectedCarArea = layerLinkedCar;
      }
    });
  }

  const linked = [];
  const unlinked = [];

  if (carList.length > 0) {
    carList.forEach(car => {
      car.is_vinculado ? linked.push(car) : unlinked.push(car);
    });
  }

  return (
    <>
      <div className="car">
        <div className="tile is-12 is-vertical">
          <h3>{stepLabel}</h3>

          <p>{formatMessage({ id: "vinculacao-car-descricao" })}</p>
        </div>
        <div className="tile is-ancestor">
          <div className="tile is-3 is-vertical">
            {loading && <div className="loader" />}

            {!loading && (
              <>
                <p className="car-list-header">
                  {formatMessage({ id: "car-vinculados" })}
                </p>
                <div className="car-list">
                  <p>
                    {formatMessage({ id: "car-vinculados-selecione-abaixo" })}
                  </p>
                  {linked.length === 0 ? (
                    <p>{formatMessage({ id: "sem-car-vinculados" })}</p>
                  ) : (
                    linked.map((car, carIndex) => (
                      <CarItem
                        key={car.gid}
                        carIndex={carIndex}
                        car={car}
                        selectedCarId={selectedCar?.gid}
                        setSelectedCar={setSelectedCar}
                      />
                    ))
                  )}
                </div>

                <p className="car-list-header">
                  {formatMessage({ id: "car-nao-vinculados" })}
                </p>
                <div className="car-list">
                  <p>
                    {formatMessage({
                      id: "car-nao-vinculados-selecione-abaixo"
                    })}
                  </p>
                  {unlinked.length === 0 ? (
                    <p>{formatMessage({ id: "sem-car-nao-vinculados" })}</p>
                  ) : (
                    unlinked.map((car, carIndex) => (
                      <CarItem
                        key={car.gid}
                        carIndex={carIndex}
                        car={car}
                        selectedCarId={selectedCar?.gid}
                        setSelectedCar={setSelectedCar}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </div>
          <div className="tile is-9 is-vertical">
            {selectedCar ? (
              <>
                {selectedCar.is_vinculado ? (
                  <LinkedCar
                    car={selectedCar}
                    areas={areas}
                    carInformationArea={selectedCarArea}
                    reload={fetchResults}
                  />
                ) : (
                  <UnlinkedCar
                    car={selectedCar}
                    reload={fetchResults}
                    setError={setError}
                  />
                )}
              </>
            ) : (
              <div className="area-map map-placeholder">
                {formatMessage({ id: "selecione-car-para-ver" })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OperationCarLinking;
